import { Route, Routes } from '@angular/router';
import { authGuard } from '@ess-front/shared/auth';
import { UserRole } from '@ess-front/shared/models';
import { UserTranslationResolver } from '@app/utils/resolvers/user-translation.resolver';
import { TripTranslationResolver } from '@app/utils/resolvers/trip-translation.resolver';
import { DefaultTranslationResolver } from '@app/utils/resolvers/default-translation.resolver';
import { ParamTranslationResolver } from '@app/utils/resolvers/param-translation-resolver.service';
import { UserHashTranslationResolver } from '@app/utils/resolvers/user-hash-translation.resolver';
import { LoginGuard } from '@app/utils/guards/login.guard';
import { brandGuard } from '@app/utils/guards/brand.guard';
import { onboardingGuard } from '@app/utils/guards/onboarding.guard';
import { membersAreaOnboardingGuard } from '@app/utils/guards/members-area-onboarding.guard';
import { membersAreaProfileLeaveGuard } from '@app/utils/guards/members-area-profile-leave.guard';
import { DialogType } from '@app/utils/models/dialog-type.model';
import {
  CAR_RENTAL_ALTERNATIVES_PATH,
  FLIGHT_ALTERNATIVES_PATH,
  TRANSFER_ALTERNATIVES_PATH,
  TRANSPORTATION_ALTERNATIVES_PATH,
} from '@app/utils/constants/timeline-transportation-alternatives-paths';
import { Domain } from '@app/utils/models/domain.model';
import { FlightType } from '@app/utils/models/itinerary/flight-type.model';
import { TransportationTab } from '@app/utils/models/itinerary/transportation-tab.model';

// Every experience dialog is a child route two segments deep under its parent tab (e.g.
// voucher/:experienceId), so ItineraryExperienceDialogHandlerComponent can always close with
// ['../..'] regardless of where it was opened from.
const experienceDialogRoute = (path: string, dialogType: DialogType): Route => ({
  path,
  loadComponent: () =>
    import('@app/itinerary-v2/shared/ui/itinerary-experience-dialog-handler/itinerary-experience-dialog-handler.component').then(
      m => m.ItineraryExperienceDialogHandlerComponent,
    ),
  data: { dialogType },
});

// Dialogs owned by the itinerary shell rather than by a single experience.
const itineraryDialogRoute = (path: string, dialogType: DialogType): Route => ({
  path,
  loadComponent: () =>
    import('@app/itinerary-v2/shared/ui/itinerary-v2-dialog-handler/itinerary-v2-dialog-handler.component').then(
      m => m.ItineraryV2DialogHandlerComponent,
    ),
  data: { dialogType },
});

const carRentalAlternativesRoute = experienceDialogRoute(
  'alternatives/:experienceId',
  DialogType.CarRentalAlternatives,
);
const experienceAlternativesRoute = experienceDialogRoute(
  'experience-alternatives/:experienceId',
  DialogType.ExperienceAlternatives,
);
const experienceDetailsRoute = experienceDialogRoute('experience/:experienceId', DialogType.ExperienceDetails);
const experienceVoucherRoute = experienceDialogRoute('voucher/:experienceId', DialogType.ExperienceVoucher);
const flightAlternativesRoute = experienceDialogRoute('alternatives/:experienceId', DialogType.FlightAlternatives);
const genericTransportationAlternativesRoute = experienceDialogRoute(
  'alternatives/:experienceId',
  DialogType.GenericTransportationAlternatives,
);
const transferAlternativesRoute = experienceDialogRoute('alternatives/:experienceId', DialogType.TransferAlternatives);

// The Timeline day lists every transportation type at once, so each Transportations dialog needs its
// own path there instead of the shared 'alternatives' segment.
const timelineCarRentalAlternativesRoute = experienceDialogRoute(
  `${CAR_RENTAL_ALTERNATIVES_PATH}/:experienceId`,
  DialogType.CarRentalAlternatives,
);
const timelineFlightAlternativesRoute = experienceDialogRoute(
  `${FLIGHT_ALTERNATIVES_PATH}/:experienceId`,
  DialogType.FlightAlternatives,
);
const timelineTransferAlternativesRoute = experienceDialogRoute(
  `${TRANSFER_ALTERNATIVES_PATH}/:experienceId`,
  DialogType.TransferAlternatives,
);
const timelineTransportationAlternativesRoute = experienceDialogRoute(
  `${TRANSPORTATION_ALTERNATIVES_PATH}/:experienceId`,
  DialogType.GenericTransportationAlternatives,
);

const itineraryV2Children: Routes = [
  { path: '', redirectTo: 'welcome', pathMatch: 'full' },
  // LEGACY ITINERARY REDIRECTS
  // Children rather than root routes: Angular applies only one redirect per config level, but
  // re-enables them for children, so these still fire after a `trip-itinerary`/`proposal`/`itinerary`
  // prefix redirect. Must stay above the `**` below, which would otherwise absorb them.
  { path: 'details/:experienceId', redirectTo: 'experiences/voucher/:experienceId', pathMatch: 'full' },
  { path: 'experience/:experienceId', redirectTo: 'experiences/experience/:experienceId', pathMatch: 'full' },
  { path: 'experience/:experienceId/details', redirectTo: 'experiences/voucher/:experienceId', pathMatch: 'full' },
  { path: 'checkout/payment-details', redirectTo: 'payment-details', pathMatch: 'full' },
  // Sibling of the layout, not a child: welcome renders without header or mobile navigation.
  {
    path: 'welcome',
    loadComponent: () =>
      import('@app/itinerary-v2/welcome/ui/itinerary-welcome/itinerary-welcome.component').then(
        m => m.ItineraryWelcomeComponent,
      ),
  },
  // Chat deeplink, no view of its own: ItineraryChatDomainService reads isChatRoute off the shell's
  // activated child, hands the URL back to the timeline, then opens the popup expanded. Must stay a
  // direct child — the empty-path layout below would hide it from firstChild. Guarded so a signed-out
  // visitor logs in and comes back here.
  {
    path: 'chat',
    canActivate: [authGuard([UserRole.MEMBER, UserRole.STAFF], 'login')],
    data: { isChatRoute: true },
    // Required, not leftover: a route with no component, redirectTo or loadChildren fails Angular's
    // config validation (NG04014) unless children is present, and dev mode is the only build that checks.
    children: [],
  },
  // Empty path: adds no URL segment, so the paths below and their relative navigations are unchanged.
  {
    path: '',
    loadComponent: () =>
      import('@app/itinerary-v2/shared/ui/itinerary-layout/itinerary-layout.component').then(
        m => m.ItineraryLayoutComponent,
      ),
    children: [
      {
        path: 'timeline',
        loadComponent: () =>
          import('@app/itinerary-v2/timeline/ui/itinerary-timeline/itinerary-timeline.component').then(
            m => m.ItineraryTimelineComponent,
          ),
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: 'overview',
            loadComponent: () =>
              import('@app/itinerary-v2/timeline/ui/timeline-overview/timeline-overview.component').then(
                m => m.TimelineOverviewComponent,
              ),
          },
          {
            path: ':dayIndex',
            loadComponent: () =>
              import('@app/itinerary-v2/timeline/ui/timeline-day/timeline-day.component').then(
                m => m.TimelineDayComponent,
              ),
            children: [
              experienceAlternativesRoute,
              experienceDetailsRoute,
              experienceVoucherRoute,
              timelineCarRentalAlternativesRoute,
              timelineFlightAlternativesRoute,
              timelineTransferAlternativesRoute,
              timelineTransportationAlternativesRoute,
            ],
          },
        ],
      },
      {
        path: 'experiences',
        loadComponent: () =>
          import('@app/itinerary-v2/experiences/ui/itinerary-experiences/itinerary-experiences.component').then(
            m => m.ItineraryExperiencesComponent,
          ),
        children: [experienceDetailsRoute, experienceVoucherRoute],
      },
      {
        path: 'accommodations',
        loadComponent: () =>
          import('@app/itinerary-v2/accommodations/ui/itinerary-accommodations/itinerary-accommodations.component').then(
            m => m.ItineraryAccommodationsComponent,
          ),
        children: [experienceAlternativesRoute, experienceDetailsRoute, experienceVoucherRoute],
      },
      {
        path: 'transportations',
        loadComponent: () =>
          import('@app/itinerary-v2/transportations/ui/itinerary-transportations/itinerary-transportations.component').then(
            m => m.ItineraryTransportationsComponent,
          ),
        children: [
          { path: '', redirectTo: 'flights', pathMatch: 'full' },
          {
            path: TransportationTab.Flights,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-flights/transportations-flights.component').then(
                m => m.TransportationsFlightsComponent,
              ),
            data: { flightType: FlightType.Round },
            children: [flightAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.ConnectingFlights,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-flights/transportations-flights.component').then(
                m => m.TransportationsFlightsComponent,
              ),
            data: { flightType: FlightType.OneWay },
            children: [flightAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.CarRentals,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-car-rentals/transportations-car-rentals.component').then(
                m => m.TransportationsCarRentalsComponent,
              ),
            children: [carRentalAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.Transfers,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-transfers/transportations-transfers.component').then(
                m => m.TransportationsTransfersComponent,
              ),
            children: [transferAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.Trains,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-generic-transportations/transportations-generic-transportations.component').then(
                m => m.TransportationsGenericTransportationsComponent,
              ),
            data: { transportationTab: TransportationTab.Trains },
            children: [genericTransportationAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.Buses,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-generic-transportations/transportations-generic-transportations.component').then(
                m => m.TransportationsGenericTransportationsComponent,
              ),
            data: { transportationTab: TransportationTab.Buses },
            children: [genericTransportationAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.Boats,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-generic-transportations/transportations-generic-transportations.component').then(
                m => m.TransportationsGenericTransportationsComponent,
              ),
            data: { transportationTab: TransportationTab.Boats },
            children: [genericTransportationAlternativesRoute, experienceVoucherRoute],
          },
          {
            path: TransportationTab.Others,
            loadComponent: () =>
              import('@app/itinerary-v2/transportations/ui/transportations-generic-transportations/transportations-generic-transportations.component').then(
                m => m.TransportationsGenericTransportationsComponent,
              ),
            data: { transportationTab: TransportationTab.Others },
            children: [genericTransportationAlternativesRoute, experienceVoucherRoute],
          },
        ],
      },
      {
        path: 'checkout',
        canActivate: [authGuard([UserRole.MEMBER, UserRole.STAFF], 'login')],
        loadComponent: () =>
          import('@app/itinerary-v2/checkout/ui/itinerary-checkout/itinerary-checkout.component').then(
            m => m.ItineraryCheckoutComponent,
          ),
        children: [
          itineraryDialogRoute('add-card', DialogType.AddCard),
          itineraryDialogRoute('travelers', DialogType.Travelers),
        ],
      },
      {
        ...itineraryDialogRoute('payment-details', DialogType.PaymentDetails),
        canActivate: [authGuard([UserRole.MEMBER, UserRole.STAFF], 'login')],
      },
    ],
  },
  // Unsupported paths under the itinerary shell land on the itinerary, not the global 404.
  // Must stay last: anything added below it is unreachable.
  { path: '**', redirectTo: 'timeline/overview' },
];

export const routes: Routes = [
  { path: '', redirectTo: 'members-area', pathMatch: 'full' },
  { path: 'members-area/login', redirectTo: 'login', pathMatch: 'full' },
  // Former path of the story details dialog, kept so saved links still open the story.
  { path: 'members-area/stories/:storySlug', redirectTo: 'stories/:storySlug', pathMatch: 'full' },
  {
    path: 'visaap',
    pathMatch: 'full',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    data: {
      brand: 'visaap',
    },
  },
  {
    path: 'visaap/:lang',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    data: {
      brand: 'visaap',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'apply/:brand',
    pathMatch: 'full',
    canActivate: [brandGuard],
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'apply/:brand/:lang',
    canActivate: [brandGuard],
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'apply',
    loadComponent: () => import('@app/apply/ui/apply.component').then(x => x.ApplyComponent),
  },
  {
    path: 'magic-link',
    canActivate: [LoginGuard],
    loadComponent: () => import('@app/auth/ui/magic-link/magic-link.component').then(x => x.MagicLinkComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'payments/:paymentResult',
    loadComponent: () =>
      import('@app/payments/ui/payments-message/payments-messages.component').then(m => m.PaymentsMessagesComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'preferences-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form/preferences-form.component').then(x => x.PreferencesFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    path: 'preferences-form-completed',
    loadComponent: () =>
      import('@app/forms/ui/preferences-form-completed/preferences-form-completed.component').then(
        x => x.PreferencesFormCompletedComponent,
      ),
  },
  // EXPERIENCE - DEEPLINK - REDIRECTS
  { path: 'hotels/:slug', redirectTo: 'hotels/:slug/en', pathMatch: 'full' },
  { path: 'experiences/:slug', redirectTo: 'experiences/:slug/en', pathMatch: 'full' },
  { path: 'restaurants/:slug', redirectTo: 'restaurants/:slug/en', pathMatch: 'full' },
  { path: 'places/:slug', redirectTo: 'places/:slug/en', pathMatch: 'full' },
  // EXPERIENCE - DEEPLINK
  {
    path: 'hotels/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'hotel',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'experiences/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'experience',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'restaurants/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'restaurant',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  {
    path: 'places/:slug/:lang',
    loadComponent: () => import('@app/experience/ui/experience/experience.component').then(m => m.ExperienceComponent),
    data: {
      endpoint: 'place',
    },
    resolve: {
      language: ParamTranslationResolver,
    },
  },
  // ITINERARY V2
  {
    path: 'trip/:hash',
    loadComponent: () =>
      import('@app/itinerary-v2/shared/ui/itinerary-v2/itinerary-v2.component').then(m => m.ItineraryV2Component),
    data: { domain: Domain.TRIP },
    resolve: {
      language: TripTranslationResolver,
    },
    children: itineraryV2Children,
  },
  {
    path: 'sample-trip/:hash',
    loadComponent: () =>
      import('@app/itinerary-v2/shared/ui/itinerary-v2/itinerary-v2.component').then(m => m.ItineraryV2Component),
    data: { domain: Domain.SAMPLE_TRIP },
    resolve: {
      language: TripTranslationResolver,
    },
    children: itineraryV2Children,
  },
  // BACKWARDS COMPATIBILITY REDIRECTS
  // Prefixes that cannot collide with `trip/:hash`, so order does not matter here. Trailing segments
  // are carried over, so `/itinerary/:hash/chat` needs no entry of its own — and because the legacy
  // paths live in `itineraryV2Children`, the aliases inherit those too.
  { path: 'trip-itinerary/:hash', redirectTo: 'trip/:hash', pathMatch: 'prefix' },
  { path: 'proposal/:hash', redirectTo: 'trip/:hash', pathMatch: 'prefix' },
  { path: 'itinerary/:hash', redirectTo: 'trip/:hash', pathMatch: 'prefix' },
  {
    path: 'survey/:hash',
    loadChildren: () =>
      import('@app/forms/ui/satisfaction-survey/satisfaction-survey.module').then(m => m.SatisfactionSurveyModule),
    resolve: {
      language: TripTranslationResolver,
    },
  },
  {
    path: 'forbidden',
    loadComponent: () => import('@app/auth/ui/forbidden/forbidden.component').then(x => x.ForbiddenComponent),
  },
  {
    path: 'onboarding',
    canActivate: [authGuard(UserRole.MEMBER, 'login'), onboardingGuard],
    loadComponent: () => import('@app/onboarding/ui/onboarding/onboarding.component').then(m => m.OnboardingComponent),
    resolve: {
      language: UserTranslationResolver,
    },
  },
  {
    path: 'members-area',
    canActivate: [authGuard(UserRole.MEMBER, 'login'), membersAreaOnboardingGuard],
    loadComponent: () =>
      import('@app/users-area/ui/members-area/members-area.component').then(m => m.MembersAreaComponent),
    resolve: {
      language: UserTranslationResolver,
    },
    children: [
      { path: '', redirectTo: 'stories', pathMatch: 'full' },
      // Former path of the top-level options dialog, kept so saved links still open it.
      { path: 'tl/:topLevelTripHash', redirectTo: 'trips/tl/:topLevelTripHash', pathMatch: 'full' },
      {
        path: 'stories',
        loadComponent: () =>
          import('@app/users-area/ui/members-area-stories/members-area-stories.component').then(
            m => m.MembersAreaStoriesComponent,
          ),
      },
      // Chat deeplink, no view of its own: MembersAreaComponent reads isChatRoute off its activated
      // child, hands the URL back to the stories, then opens the chat expanded.
      {
        path: 'chat',
        data: { isChatRoute: true },
        // Required: a route with no component, redirectTo or loadChildren fails config validation (NG04014).
        children: [],
      },
      {
        path: 'trips',
        loadComponent: () =>
          import('@app/users-area/ui/members-area-trips/members-area-trips.component').then(
            m => m.MembersAreaTripsComponent,
          ),
        // A child of trips, so the trips page stays mounted (and its trips loaded) behind the dialog.
        children: [
          {
            path: 'tl/:topLevelTripHash',
            loadComponent: () =>
              import('@app/users-area/ui/dialog-handler/users-area-dialog-handler.component').then(
                mod => mod.UsersAreaDialogHandlerComponent,
              ),
            data: { dialogType: DialogType.TopLevelTrip, domain: Domain.MEMBERS_AREA },
          },
        ],
      },
      {
        path: 'profile',
        canDeactivate: [membersAreaProfileLeaveGuard],
        loadComponent: () =>
          import('@app/users-area/ui/members-area-profile/members-area-profile.component').then(
            m => m.MembersAreaProfileComponent,
          ),
      },
      {
        path: 'request-trip',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/users-area-dialog-handler.component').then(
            mod => mod.UsersAreaDialogHandlerComponent,
          ),
        data: { dialogType: DialogType.RequestTrip, domain: Domain.MEMBERS_AREA },
      },
      {
        path: 'schedule-a-call',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/users-area-dialog-handler.component').then(
            mod => mod.UsersAreaDialogHandlerComponent,
          ),
        data: { dialogType: DialogType.ScheduleCall, domain: Domain.MEMBERS_AREA },
      },
    ],
  },
  // Public: anyone can read a story, signed in or not.
  {
    path: 'stories/:storySlug',
    loadComponent: () =>
      import('@app/users-area/ui/story-details-page/story-details-page.component').then(
        m => m.StoryDetailsPageComponent,
      ),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'staff-area',
    canActivate: [authGuard(UserRole.STAFF, 'login')],
    loadComponent: () => import('@app/users-area/ui/staff-area/staff-area.component').then(m => m.StaffAreaComponent),
    title: 'Essentialist - Staff Area',
    resolve: {
      language: UserTranslationResolver,
    },
    children: [
      {
        path: 'tl/:topLevelTripHash',
        loadComponent: () =>
          import('@app/users-area/ui/dialog-handler/users-area-dialog-handler.component').then(
            mod => mod.UsersAreaDialogHandlerComponent,
          ),
        data: { dialogType: DialogType.TopLevelTrip, domain: Domain.STAFF_AREA },
      },
    ],
  },
  {
    path: 'login',
    canActivate: [LoginGuard],
    loadComponent: () => import('@app/auth/ui/login/login.component').then(x => x.LoginComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'reset-password/:token/:uid',
    loadComponent: () =>
      import('@app/auth/ui/reset-password/reset-password.component').then(x => x.ResetPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'forgot-password',
    canActivate: [LoginGuard],
    loadComponent: () =>
      import('@app/auth/ui/forgot-password/forgot-password.component').then(x => x.ForgotPasswordComponent),
    resolve: {
      language: DefaultTranslationResolver,
    },
  },
  {
    path: 'authorization-form/:hash',
    loadComponent: () =>
      import('@app/forms/ui/authorization-form/authorization-form.component').then(x => x.AuthorizationFormComponent),
    resolve: {
      language: UserHashTranslationResolver,
    },
  },
  {
    path: '**',
    loadComponent: () => import('@app/auth/ui/not-found/not-found.component').then(x => x.NotFoundComponent),
  },
];
